import { $generateNodesFromDOM } from "@lexical/html";
import { $createParagraphNode, $createTextNode, $getRoot, $insertNodes, $isElementNode, $isParagraphNode } from "lexical";
import { $isTableNode } from "../nodes/CustomTableNode/src";
import { generateStableCaptionId } from "../plugins/TablePlugin";
import { ColoredNode } from "../nodes/TableNode";
import { $getPageRoot, $isLayoutContainerNode } from "../nodes/LayoutContainerNode";
import { $isImageNode, findImageNodes } from "../nodes/ImageNode";
import { generateStableImageCaptionId } from "../plugins/ImagesPlugin";

export function parseHtmlStateWithStyle(html) {
    // Create a temporary container to parse the HTML string
    const container = document.createElement('div');
    container.innerHTML = html;

    // Find all elements in the parsed HTML
    const elements = container.querySelectorAll('*'); 

    elements.forEach(element => {
        // Create a variable to hold inline styles
        let inlineStyle = '';

        // Special case: If the element has both italic and strikethrough classes
        if (
            element.classList.contains('PlaygroundEditorTheme__textItalic') &&
            element.classList.contains('PlaygroundEditorTheme__textStrikethrough')
        ) {
            // Apply the strikethrough as an inline style
            inlineStyle += 'text-decoration: line-through; ';

            // Wrap the content in <span> for the inline style
            const spanTag = document.createElement('span');
            spanTag.style.cssText = inlineStyle.trim(); // Apply line-through style here

            const iTag = document.createElement('i');
            iTag.innerHTML = element.innerHTML;

            // Wrap the content inside <i> and <span> tags
            spanTag.appendChild(iTag);

            // Replace original content with the new wrapped structure
            element.innerHTML = '';
            element.appendChild(spanTag);
        }
        // Special case 2: If the element has bold and italic and strikethrough classes
        else if (
            element.classList.contains('PlaygroundEditorTheme__textBold') &&
            element.classList.contains('PlaygroundEditorTheme__textItalic') &&
            element.classList.contains('PlaygroundEditorTheme__textStrikethrough')
        ) {
            // Apply the strikethrough as an inline style
            inlineStyle += 'text-decoration: line-through; ';

            // Wrap the content in <span> for the inline style
            const spanTag = document.createElement('span');
            spanTag.style.cssText = inlineStyle.trim(); // Apply line-through style here

            const iTag = document.createElement('i');
            const strongTag = document.createElement('strong');

            // Wrap the content inside each tag in order
            strongTag.innerHTML = element.innerHTML;
            iTag.appendChild(strongTag);
            spanTag.appendChild(iTag);

            // Replace original content with the new wrapped structure
            element.innerHTML = '';
            element.appendChild(spanTag);
        }
        // For other cases: Apply bold, italic, underline, and strikethrough separately
        else {
            // Check for strikethrough class and apply inline style
            if (element.classList.contains('PlaygroundEditorTheme__textStrikethrough')) {
                inlineStyle += 'text-decoration: line-through; '; // Add strikethrough as an inline style
            }

            if (element.classList.contains('PlaygroundEditorTheme__textUnderlineStrikethrough')) {
                inlineStyle += 'text-decoration: line-through underline; '; // Add strikethrough as an inline style
            }

            // Check for bold class and wrap in <strong> tag
            if (element.classList.contains('PlaygroundEditorTheme__textBold')) {
                const strongTag = document.createElement('strong');
                strongTag.innerHTML = element.innerHTML;
                element.innerHTML = '';
                element.appendChild(strongTag);
            }

            // Check for italic class and wrap in <i> tag
            if (element.classList.contains('PlaygroundEditorTheme__textItalic')) {
                const iTag = document.createElement('i');
                iTag.innerHTML = element.innerHTML;
                element.innerHTML = '';
                element.appendChild(iTag);
            }

            // Check for underline class and wrap in <u> tag
            if (element.classList.contains('PlaygroundEditorTheme__textUnderline')) {
                const uTag = document.createElement('u');
                uTag.innerHTML = element.innerHTML;
                element.innerHTML = '';
                element.appendChild(uTag);
            }

            // If inline styles (like strikethrough) are applied, wrap the content with <span>
            if (inlineStyle) {
                const spanTag = document.createElement('span');
                spanTag.style.cssText = inlineStyle.trim();
                spanTag.innerHTML = element.innerHTML;
                element.innerHTML = '';
                element.appendChild(spanTag);
            }
        }
    });

    // Return the modified HTML as a string
    return container.innerHTML;
}



export function parseHtmlForExportToWord(html) {
    const container = document.createElement('div');
    container.innerHTML = html;

    const tables = container.querySelectorAll('.PlaygroundEditorTheme__table');

    if (tables.length === 0) {
        return html;
    }

    tables.forEach(table => {
        // Word-style: full width, grid lines
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse'; // Important for clean Word rendering
        table.style.border = '1px solid black';
        table.style.fontFamily = 'Times New Roman'; // Word default font
        table.style.fontSize = '11pt';

        const rows = table.querySelectorAll('tr');

        rows.forEach((row, rowIndex) => {
            row.style.border = '1px solid black';

            const cells = row.querySelectorAll('td, th');
            const numColumns = cells.length;
            const columnWidth = (100 / numColumns) + '%';

            cells.forEach(cell => {
                cell.style.width = columnWidth;
                cell.style.height = '20px';
                cell.style.border = '1px solid black';

                // Word-style: padding for better readability
                cell.style.padding = '5px 8px';

                // Word-style: default left-align text
                cell.style.textAlign = 'left';
                cell.style.verticalAlign = 'middle';

                // // Apply row banding: light gray background on even rows (excluding header)
                // if (rowIndex % 2 === 1 && !cell.classList.contains('PlaygroundEditorTheme__tableCellHeader')) {
                //     cell.style.backgroundColor = '#f2f2f2'; // Light gray banding
                // }

                // Fallback for empty cells
                if (!cell.innerHTML.trim()) {
                    cell.innerHTML = '&nbsp;';
                }

                // Underline and strikethrough
                const underlineElements = cell.querySelectorAll('.PlaygroundEditorTheme__textUnderline');
                underlineElements.forEach(el => {
                    el.style.textDecoration = 'underline';
                });

                const strikethroughElements = cell.querySelectorAll('.PlaygroundEditorTheme__textStrikethrough');
                strikethroughElements.forEach(el => {
                    el.style.textDecoration = 'line-through';
                });

                // Header styling (Word-style)
                if (cell.classList.contains('PlaygroundEditorTheme__tableCellHeader')) {
                    cell.style.fontWeight = 'bold';
                    cell.style.backgroundColor = '#d9d9d9'; // Word-style light gray header
                    cell.style.textAlign = 'center';
                }
            });
        });
    });

    return container.innerHTML;
}


export function htmlWithoutSpaces(html) {
    // Remove all <br> and <br/> tags
    html = html.replace(/<br\s*\/?>/g, '');

    // Remove empty <p> tags or <p> tags that only contain <br> tags
    // html = html.replace(/<p[^>]*>\s*(<br\s*\/?>)*\s*<\/p>/g, '');

    if (html.includes('Crop')) {
        html = html.replace(/Crop/g, ''); // Remove the word 'Crop'
    }

    const res = removePTagInsideH1FromString(html)
    const res2 = ensureUniqueTableIds(res);
    const finalRes = mergeTablesByTableId(res2);
    return finalRes;
}

export function ensureUniqueTableIds(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const tables = doc.querySelectorAll('table');
  const usedIds = new Set();

  tables.forEach(table => {
    let id = table.getAttribute('data-table-id');

    // Generate if missing OR duplicated
    if (!id) {
      id = generateUniqueTableGroupId();
      table.setAttribute('data-table-id', id);
    }

    usedIds.add(id);
  });

  return doc.body.innerHTML;
}


function mergeTablesByTableId(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  // Get only top-level tables
  const tables = Array.from(doc.querySelectorAll('table')).filter(
    table => !table.closest('td table, th table')
  );

  // Group tables by data-table-id
  const tableGroups = new Map();

  tables.forEach(table => {
    const tableId = table.getAttribute('data-table-id');
    if (!tableId) return;

    if (!tableGroups.has(tableId)) {
      tableGroups.set(tableId, []);
    }
    tableGroups.get(tableId).push(table);
  });

  // Merge each group
  tableGroups.forEach(groupTables => {
    if (groupTables.length <= 1) return;

    // Create merged table
    const baseTable = document.createElement('table');

    // Copy ALL attributes (do NOT remove data-table-id)
    for (const attr of groupTables[0].attributes) {
      baseTable.setAttribute(attr.name, attr.value);
    }

    // Append all rows in order
    groupTables.forEach(table => {
      Array.from(table.rows).forEach(row => {
        baseTable.appendChild(row);
      });
    });

    // Replace first table and remove the rest
    groupTables[0].replaceWith(baseTable);
    groupTables.slice(1).forEach(t => t.remove());
  });

  return doc.body.innerHTML;
}



// function splitEachRowIntoStyledTables(htmlString) {
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(htmlString, 'text/html');

//   const tables = doc.querySelectorAll('table');

//   tables.forEach((originalTable) => {
//     const rows = Array.from(originalTable.querySelectorAll('tr'));
//     if (rows.length <= 1) return;

//     const fragment = document.createDocumentFragment();

//     rows.forEach((row) => {
//       const newTable = document.createElement('table');

//       // Copy attributes (style, class, border, etc.)
//       for (let attr of originalTable.attributes) {
//         newTable.setAttribute(attr.name, attr.value);
//       }

//       // Optionally clone thead/tfoot if needed here

//       // Append cloned row
//       newTable.appendChild(row.cloneNode(true));
//       fragment.appendChild(newTable);
//     });

//     // Insert new tables and remove original
//     originalTable.parentNode.insertBefore(fragment, originalTable);
//     originalTable.remove();
//   });

//   return doc.body.innerHTML;
// }

export function generateUniqueTableGroupId() {
  return `table-group-${crypto.randomUUID().replace(/-/g, "").slice(0, 9)}`;
}

function splitEachRowIntoStyledTables(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');


    const tables = Array.from(doc.querySelectorAll('table')).filter(table => {
        return !table.closest('td table, th table');
    });

    tables.forEach((originalTable) => {
        const tableGroupId = generateUniqueTableGroupId();
        const rows = Array.from(originalTable.rows);
        if (rows.length <= 1) return;

        const fragment = document.createDocumentFragment();

        rows.forEach((row, index) => {
            const newTable = document.createElement('table');

            for (let attr of originalTable.attributes) {
                newTable.setAttribute(attr.name, attr.value);
            }

            newTable.setAttribute('data-table-caption', index === 0 ? 'true' : 'false');
            newTable.setAttribute('data-table-id', tableGroupId);

            const movedRow = row;
            newTable.appendChild(movedRow);
            fragment.appendChild(newTable);
        });

        originalTable.replaceWith(fragment);
    });

    //   const res2 = addTableCaptions(doc.body.innerHTML);
    return  indexTablesById(doc.body.innerHTML);
}

export function indexTablesById(htmlString) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    const tableCounts = {};
    const tables = tempDiv.querySelectorAll('table[data-table-id]');

    tables.forEach(table => {
        const tableId = table.getAttribute('data-table-id');
        if (!tableCounts[tableId]) {
            tableCounts[tableId] = 1;
        } else {
            tableCounts[tableId]++;
        }
        table.setAttribute('data-table', tableCounts[tableId]);
    });

    return tempDiv.innerHTML;
}


//  function flattenNestedTables(htmlString) {
//     const parser = new DOMParser();
//     const doc = parser.parseFromString(htmlString, 'text/html');
//     let caption

//     // Collect all nested tables inside other tables, in original order
//     const allTables = Array.from(doc.querySelectorAll('table'));
//     const nestedTables = allTables.filter(table => {
//       caption = table.previousElementSibling
//         return table.closest('table') !== table; // Skip self, get only nested
//     });

//     nestedTables.forEach(nestedTable => {
//         // Find outermost parent <table>
//         let parentTable = nestedTable.closest('table');
//         while (parentTable && parentTable.parentElement) {
//             const higher = parentTable.parentElement.closest('table');
//             if (!higher) break;
//             parentTable = higher;
//         }

//         // Remove nested table from its original position
//         nestedTable.remove();

//         // Insert after the outermost parent
//         parentTable.insertAdjacentElement('afterend', nestedTable);
//         nestedTable.insertAdjacentElement('beforebegin', caption);
//     });
//     const res = removeNestedTables(doc.body.innerHTML)
//     return res
// }

// function removeNestedTables(htmlString) {
//   // Create a DOM parser
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(htmlString, 'text/html');

//   // Get all table elements
//   const allTables = doc.querySelectorAll('table');

//   // Loop through each table
//   allTables.forEach((table) => {
//     // Check if this table is nested inside another table
//     let parent = table.parentElement;
//     while (parent) {
//       if (parent.tagName === 'TABLE') {
//         table.previousElementSibling.textContent.includes('Table') ? table.previousElementSibling.remove() : table.textContent
//         table.remove(); // Remove the nested table
//         break;
//       }
//       parent = parent.parentElement;
//     }
//   });

//   // Return the modified HTML as string
//   return doc.body.innerHTML;
// }






export function addTableCaptions(htmlString, styleConfig) {
  if (!htmlString || typeof htmlString !== 'string') return htmlString;
  const config = styleConfig
  try {
    const container = document.createElement('div');
    container.innerHTML = htmlString;

    const tables = container.querySelectorAll('table[data-table-caption="true"]');
    const images = container.querySelectorAll('img');
    let captionCount = 1;
    let figureCount = 1;

    const TABLE_PREFIX_RE = /^\s*Table\s+\d+:\s*/i;

    const nodeContainsDecorator = (el) => {
      if (!(el instanceof Element)) return false;
      if (
        el.hasAttribute('data-lexical-decorator') ||
        el.getAttribute('data-node-type') === 'reference' ||
        el.hasAttribute('data-cross-ref')
      ) return true;
      // Check descendants
      return !!el.querySelector('[data-lexical-decorator], [data-node-type="reference"], [data-cross-ref]');
    };

    const ensureCaptionP = (table) => {
      let prevElem = table.previousElementSibling;
      if (prevElem && prevElem.tagName && prevElem.tagName.toLowerCase() === 'p') return prevElem;
      const p = document.createElement('p');
      table.parentNode.insertBefore(p, table);
      return p;
    };

    const upsertNumberSpan = (p, count) => {
      let numberSpan = p.querySelector('[data-ref-id]');

      if (numberSpan) {
        const parts = (numberSpan.textContent || '').split(/:\s*/);
        const existingText = parts.slice(1).join(': ');
        numberSpan.textContent = `Table ${count}: ${existingText}`;
        if (p.firstChild !== numberSpan) {
          p.insertBefore(numberSpan, p.firstChild);
        }
        return p;
      }

      numberSpan = document.createElement('span');
      numberSpan.setAttribute('data-ref-id', 'table-caption');

      const existingText = (p.textContent || '').trim();
      numberSpan.textContent = existingText
        ? `Table ${count}: ${existingText}`
        : `Table ${count}: `;

      p.innerHTML = '';
      p.appendChild(numberSpan);

      return p;
    };




    const getCaptionStyle = (styleConfig) =>
      styleConfig.heading.find(h => h.title === "Captions");


    const applyCaptionStyles = (els, captionStyle) => {
      if (!captionStyle || !els) return;
      const apply = (el) => {
        el.style.setProperty("font-family", captionStyle.fontFamily, "important");
        el.style.setProperty("font-size", `${captionStyle.fontSize}px`, "important");
        el.style.setProperty(
          "color",
          captionStyle.fontColor || captionStyle.color,
          "important"
        );
        el.style.setProperty(
          "font-weight",
          captionStyle.bold === "Yes" ? "bold" : "normal",
          "important"
        );
        el.style.setProperty(
          "font-style",
          captionStyle.italic === "Yes" ? "italic" : "normal",
          "important"
        );
        el.style.setProperty(
          "line-height",
          captionStyle.lineSpacing,
          "important"
        );
        el.style.setProperty(
          "text-align",
          captionStyle.alignment?.toLowerCase(),
          "important"
        );
        el.style.setProperty(
          "margin-top",
          `${captionStyle.leadingSpace}em`,
          "important"
        );
        el.style.setProperty(
          "margin-bottom",
          `${captionStyle.trailingSpace}em`,
          "important"
        );
      };

      // Apply to paragraph itself
      apply(els);
      // Apply to ALL child elements (including nested)
      els.querySelectorAll("*").forEach(apply);
    };

    tables.forEach((table) => {
      if (!table || !table.parentNode) return;

      const captionP = ensureCaptionP(table);

      // Now add/update the canonical number span
      const numberSpan = upsertNumberSpan(captionP, captionCount);
      const captionStyle = getCaptionStyle(config);
      applyCaptionStyles(numberSpan, captionStyle);

      captionCount++;
    });
    const res = splitLongTableCells(container.innerHTML);
    return container.innerHTML;
  } catch (error) {
    console.error('Error in addTableCaptions:', error);
    const res = repairTables(htmlString)
    return res;
  }
}

export function repairTables(htmlString) {
  // Create a DOM parser to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Select all th and td elements inside tables
  const cells = doc.querySelectorAll('table th, table td');

  cells.forEach(cell => {
    // Collect direct child nodes that are not <p> elements
    const nodesToWrap = [];

    cell.childNodes.forEach(child => {
      // Only wrap if not already a <p> element and is an element or text node with non-empty content
      if (
        !(child.nodeType === 1 && child.tagName.toLowerCase() === 'p') &&
        !(child.nodeType === 3 && !child.textContent.trim() === '') // skip empty text nodes
      ) {
        nodesToWrap.push(child);
      }
    });

    // Wrap each of those nodes inside a <p>
    nodesToWrap.forEach(node => {
      const p = doc.createElement('p');
      // Move the node inside <p>
      p.appendChild(node.cloneNode(true));
      cell.replaceChild(p, node);
    });
  });

  // Return the updated HTML as a string (innerHTML of body)
  return doc.body.innerHTML;
}




function splitLongTableCells(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const tables = Array.from(doc.querySelectorAll('table'));

    tables.forEach(originalTable => {
        const originalRows = Array.from(originalTable.querySelectorAll('tr'));
        let hasLongCell = false;
                  function containsImageTag(str) {
          return /<img\b[^>]*>/i.test(str);
         }

        // Check for long content
        originalRows.forEach(row => {
            row.querySelectorAll('td, th').forEach(cell => {
                if (cell.textContent.trim().length > 700) {
                    hasLongCell = true;
                }
                if(containsImageTag(cell.innerHTML)){
                    hasLongCell = false
                }
            });
        });

        if (hasLongCell) {
            const newTables = [];

            originalRows.forEach(originalRow => {
                const originalCells = Array.from(originalRow.children); // td or th

                const chunks = originalCells.map(cell => {
                    const text = cell.textContent.trim();
                    const splitText = [];

                    for (let i = 0; i < text.length; i += 700) {
                        splitText.push(text.slice(i, i + 700));
                    }

                    return {
                        tagName: cell.tagName.toLowerCase(),
                        attributes: [...cell.attributes],
                        chunks: splitText
                    };
                });

                const maxChunks = Math.max(...chunks.map(c => c.chunks.length));

                for (let i = 0; i < maxChunks; i++) {
                    let newTable = newTables[i];
                    if (!newTable) {
                        newTable = doc.createElement('table');
                        [...originalTable.attributes].forEach(attr => {
                            if (attr.name === 'data-table-caption' && attr.value === 'true') {
                                newTable.setAttribute(attr.name, 'false')
                            } else {
                                newTable.setAttribute(attr.name, attr.value)
                            }
                        }
                        );
                        newTables[i] = newTable;
                    }

                    const newRow = doc.createElement('tr');
                    [...originalRow.attributes].forEach(attr =>
                        newRow.setAttribute(attr.name, attr.value)
                    );

                    chunks.forEach(cellData => {
                        const newCell = doc.createElement(cellData.tagName);
                        cellData.attributes.forEach(attr =>
                            newCell.setAttribute(attr.name, attr.value)
                        );
                        newCell.textContent = cellData.chunks[i] || '';
                        newRow.appendChild(newCell);
                    });

                    newTable.appendChild(newRow);
                }
            });

            // Replace original table with new tables as HTML string
            const wrapper = doc.createElement('div');
            newTables.forEach(table => wrapper.appendChild(table));

            // Replace the original table in its parent
            originalTable.replaceWith(...wrapper.children);
        }
    });

    return doc.body.innerHTML;
}






export function removePTagInsideH1FromString(htmlString) {
    // Create a temporary DOM element to parse the HTML string
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    // Select all h1 elements in the parsed HTML
    const h1Elements = tempDiv.querySelectorAll('h1');

    h1Elements.forEach(h1 => {
        // Find the first p tag inside each h1 element
        const pInsideH1 = h1.querySelector('p');

        // Remove the p tag if it exists inside the h1 tag
        if (pInsideH1) {
            pInsideH1.remove();
        }
    });

    // const result = filterHiddenParagraphs(tempDiv.innerHTML);
    const result = wrapInlineElements(tempDiv.innerHTML);
    // Return the updated HTML as a string
    return result;
}

function wrapInlineElements(html) {
  // Create a temporary container
  const container = document.createElement('div');
  container.innerHTML = html;

  // Define inline tags that need wrapping
  const inlineTags = ['SPAN', 'STRONG', 'EM'];

  // Function to check if a node is block-level
  function isBlockElement(node) {
    return node.nodeType === 1 && node.tagName === 'P';
  }

  // Iterate over container children
  const children = Array.from(container.childNodes);

  children.forEach(node => {
    // Only wrap inline tags or text nodes that are direct children of container
    if (
      (node.nodeType === 1 && inlineTags.includes(node.tagName)) ||
      node.nodeType === 3 // text node
    ) {
      const p = document.createElement('p');
      node.replaceWith(p);
      p.appendChild(node);
    }
  });

  return container.innerHTML;
}




export function transformHeadings(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const processElements = (elements) => {
        elements.forEach(el => {
            const onlyChildIsTable =
                el.children.length > 0 &&
                Array.from(el.children).every(child => child.tagName === 'TABLE');

            // Case 1: Element wraps only table(s) → unwrap
            if (onlyChildIsTable) {
                const fragment = document.createDocumentFragment();
                Array.from(el.children).forEach(child => {
                    if (child.tagName === 'TABLE') {
                        fragment.appendChild(child.cloneNode(true));
                    }
                });
                el.replaceWith(fragment);
            }

            // Case 2: Element contains only a <p> (possibly empty or just <br/>)
            else if (
                el.children.length === 1 &&
                el.children[0].tagName === 'P'
            ) {
                const p = el.children[0];
                const pHtml = p.innerHTML.trim();
                const isEmptyOrBrOnly = pHtml === '' || /^<br\s*\/?>$/i.test(pHtml);

                if (isEmptyOrBrOnly) {
                    el.replaceWith(p);
                } else {
                    p.replaceWith(...p.childNodes);
                }
            }
        });
    };

    // Select all headings and <p> elements
    const elementsToProcess = Array.from(
        doc.querySelectorAll('h1, h2, h3, h4, h5, h6, p')
    );

    processElements(elementsToProcess);

    return doc.body.innerHTML;
}

export const PastingData = (editor,enableCaptions,styleConfig,showPagination) => {
    editor.update(() => {
            const CaptionStyle = styleConfig?.heading?.filter((item) => item.title === 'Captions')
            const CaptionStyle_ = CaptionStyle && CaptionStyle[0];

            setTimeout(()=>{
              editor.update(()=>{
            if (enableCaptions) {
              const rootNode = showPagination ? $getPageRoot() : $getRoot();
              if (!rootNode) return;
              const tableNodes = ($isLayoutContainerNode(rootNode) ? rootNode.getAllChildren() : rootNode.getChildren()).filter($isTableNode);
              let index1 = 1;

              tableNodes.forEach((tableNode) => {
                const previousNode = tableNode.getPreviousSibling();
                const rootNode = showPagination ? $getPageRoot() : $getRoot();
                if (!rootNode) return;
                const tables = ($isLayoutContainerNode(rootNode) ? rootNode.getAllChildren() : rootNode.getChildren()).filter($isTableNode);
                const countOfTable = tables.length;

                if(tableNode.__groupId === ''){
                  tableNode.setGroupId(generateUniqueTableGroupId())
                }

                const existingCaptionId = (tableNode).__captionId ||
                  generateStableCaptionId(`Table ${index1}`, countOfTable);

                const nextNode = tableNode.getNextSibling();
                if (nextNode && $isParagraphNode(nextNode)) {
                  nextNode.getChildren().forEach((child) => {
                    if (child.getTextContent().startsWith("Table")) {
                      child.remove();
                    }
                  });
                }

                if (previousNode && $isParagraphNode(previousNode)) {
                  // Check if previous node contains a caption with ID
                  let existingLabelNode = null;
                  previousNode.getChildren().forEach((child) => {
                    if (child.getTextContent().startsWith("Table")) {
                      existingLabelNode = child;
                    }
                  });

                  if (existingLabelNode) {
                    // If we have an existing label node, update its content but keep the ID
                    const existingId = (existingLabelNode).getId?.() || existingCaptionId;

                    existingLabelNode.remove();
                    // Create new label with preserved ID
                    const newLabelNode = new ColoredNode(
                      `Table ${index1}:`,
                      CaptionStyle_?.fontColor || "black",
                      `${CaptionStyle_?.fontSize}px` || "14.5px",
                      CaptionStyle_?.bold === "Yes" ? "bold" : "normal",
                      CaptionStyle_?.italic === "Yes" ? "italic" : "normal",
                      CaptionStyle_?.alignment || "left",
                      CaptionStyle_?.fontFamily || "Times New Roman",
                      CaptionStyle_?.leadingSpace || "0",
                      CaptionStyle_?.lineSpacing || "1",
                      CaptionStyle_?.trailingSpace || "0",
                      existingId
                    );

                    const firstChild = previousNode.getFirstChild();
                    if (firstChild !== null) {
                      firstChild.insertBefore(newLabelNode);
                    } else {
                      previousNode.append(newLabelNode);
                    }
                  } else {
                    const existingId = (previousNode).getId?.() || existingCaptionId;
                    // Create new label with preserved ID
                    const newLabelNode = new ColoredNode(
                      `Table ${index1}: `,
                      CaptionStyle_?.fontColor || "black",
                      `${CaptionStyle_?.fontSize}px` || "14.5px",
                      CaptionStyle_?.bold === "Yes" ? "bold" : "normal",
                      CaptionStyle_?.italic === "Yes" ? "italic" : "normal",
                      CaptionStyle_?.alignment || "left",
                      CaptionStyle_?.fontFamily || "Times New Roman",
                      CaptionStyle_?.leadingSpace || "0",
                      CaptionStyle_?.lineSpacing || "1",
                      CaptionStyle_?.trailingSpace || "0",
                      existingId
                    );
                    const para = $createParagraphNode();
                    para.append(newLabelNode)
                    const firstChild = previousNode.getFirstChild()
                    previousNode.insertAfter(para)
                  }
                }
                else {
                  // If there's no previous paragraph node, create a new caption
                  const tableNumber = `Table ${index1}:`;
                  const newCaptionContainer = $createParagraphNode();
                  newCaptionContainer.setFormat("left");

                  const newLabelNode = new ColoredNode(
                    tableNumber,
                    CaptionStyle_?.fontColor || "black",
                    `${CaptionStyle_?.fontSize}px` || "14.5px",
                    CaptionStyle_?.bold === "Yes" ? "bold" : "normal",
                    CaptionStyle_?.italic === "Yes" ? "italic" : "normal",
                    CaptionStyle_?.alignment || "left",
                    CaptionStyle_?.fontFamily || "Times New Roman",
                    CaptionStyle_?.leadingSpace || "0",
                    CaptionStyle_?.lineSpacing || "1",
                    CaptionStyle_?.trailingSpace || "0",
                    existingCaptionId
                  );
                  newLabelNode.setStyle("font-weight: bold; margin-right: 5px;");

                  // const captionText = caption.trim() === "" ? "Caption text here" : caption;
                  const captionNode = $createTextNode(' ');
                  captionNode.setStyle("color: black; font-style: italic; font-weight: normal;");

                  newCaptionContainer.append(newLabelNode);
                  newCaptionContainer.append($createTextNode(" "));
                  newCaptionContainer.append(captionNode);
                  if(tableNode.__isCaption === true){
                    tableNode.insertBefore(newCaptionContainer)
                  }
                }

                const hasCaption = (tableNode).__isCaption === true;
                if (hasCaption) {
                  index1++;
                }
              });
            }
              })
            },500)

            setTimeout(()=>{
              editor.update(()=>{
            if (enableCaptions) {
              const rootNode = showPagination ? $getPageRoot() : $getRoot();
              if (!rootNode) return;
              const tableNodes = findImageNodes(($isLayoutContainerNode(rootNode) ? rootNode.getAllChildren() : rootNode.getChildren()))
              let index1 = 1;

              tableNodes.forEach((tableNode) => {
                const previousNode = tableNode.getPreviousSibling();
                const rootNode = showPagination ? $getPageRoot() : $getRoot();
                if (!rootNode) return;
                const tables = findImageNodes(($isLayoutContainerNode(rootNode) ? rootNode.getAllChildren() : rootNode.getChildren()));
                const countOfTable = tables.length;

                const existingCaptionId = tableNode.__id || generateStableImageCaptionId(`ayu ${index1}`, index1, '12345');
                const nextNode = tableNode.getNextSibling();
                if(nextNode){
                  let existingCaption = null
                    if(nextNode.getTextContent().startsWith('Figure')){
                      existingCaption = nextNode
                    }
                  if (existingCaption) {
                    // If we have an existing label node, update its content but keep the ID
                    const existingId = (existingCaption).getId?.() || existingCaptionId;

                    existingCaption.remove();
                    // Create new label with preserved ID
                    const newLabelNode = new ColoredNode(
                      `Figure ${index1}:`,
                      CaptionStyle_?.fontColor || "black",
                      `14.5px`,
                      CaptionStyle_?.bold === "Yes" ? "bold" : "normal",
                      CaptionStyle_?.italic === "Yes" ? "italic" : "normal",
                      CaptionStyle_?.alignment || "left",
                      CaptionStyle_?.fontFamily || "Times New Roman",
                      CaptionStyle_?.leadingSpace || "0",
                      CaptionStyle_?.lineSpacing || "1",
                      CaptionStyle_?.trailingSpace || "0",
                      existingId
                    );

                    // const firstChild = nextNode;
                    // if (firstChild !== null) {
                    //   firstChild.insertAfter(newLabelNode);
                    // } else {
                      tableNode.insertAfter(newLabelNode)
                    // }
                  } else {
                    const existingId = (nextNode).getId?.() || existingCaptionId;
                    // Create new label with preserved ID
                    const newLabelNode = new ColoredNode(
                      `Figure ${index1}: `,
                      CaptionStyle_?.fontColor || "black",
                      `14.5px`,
                      CaptionStyle_?.bold === "Yes" ? "bold" : "normal",
                      CaptionStyle_?.italic === "Yes" ? "italic" : "normal",
                      CaptionStyle_?.alignment || "left",
                      CaptionStyle_?.fontFamily || "Times New Roman",
                      CaptionStyle_?.leadingSpace || "0",
                      CaptionStyle_?.lineSpacing || "1",
                      CaptionStyle_?.trailingSpace || "0",
                      existingId
                    );
                    nextNode.replace(newLabelNode)
                  }
                }
                else{
                                   const existingId = existingCaptionId;
                    // Create new label with preserved ID
                    const newLabelNode = new ColoredNode(
                      `Figure ${index1}:`,
                      CaptionStyle_?.fontColor || "black",
                      `14.5px`,
                      CaptionStyle_?.bold === "Yes" ? "bold" : "normal",
                      CaptionStyle_?.italic === "Yes" ? "italic" : "normal",
                      CaptionStyle_?.alignment || "left",
                      CaptionStyle_?.fontFamily || "Times New Roman",
                      CaptionStyle_?.leadingSpace || "0",
                      CaptionStyle_?.lineSpacing || "1",
                      CaptionStyle_?.trailingSpace || "0",
                      tableNode.__id
                    );

                    const conatiner = $createParagraphNode();
                    conatiner.append(newLabelNode);
                    tableNode.insertAfter(newLabelNode)
                }



                const hasCaption = (tableNode).__id;
                if (hasCaption !== undefined) {
                  index1++;
                }
                let webpExists = false;
                if ($isImageNode(tableNode) && tableNode.getSrc().includes('webp')) {
                  webpExists = true
                }
                if (webpExists) {
                  window.alert('WebP image not supported. Please use PNG or JPG.')
                  const paragraph = $createParagraphNode();
                  const text = $createTextNode('⚠️Unsupported WebP image. Please use PNG or JPG format.');
                  text.setStyle('color: red; font-size: 14px;');
                  paragraph.append(text);
                  paragraph.append();
                  tableNode.replace(paragraph);

                }
              });
            }
              })
            },500)
    })
  }


   export function alternateTableRowColors(html, oddRowColor = '#fff', evenRowColor = '#ffffff') {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
  
        const tables = tempDiv.querySelectorAll('table');
  
        tables.forEach(table => {
          const rows = table.querySelectorAll('tr');
  
          rows.forEach((row, index) => {
            if (row.closest('thead')) {
              const cells = row.querySelectorAll('td');
  
              cells.forEach(cell => {
                cell.style.backgroundColor = oddRowColor;
              });
            }
  
            const cells = row.querySelectorAll('td');
  
            if (index % 2 === 0) {
              cells.forEach(cell => {
                cell.style.backgroundColor = evenRowColor;
              });
            }else{
                            cells.forEach(cell => {
                cell.style.backgroundColor = oddRowColor;
              });
            }
          });
        });
  
        return tempDiv.innerHTML;
      }